import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from "react-router-dom";
import apiClient from "../utils/apiClient";

const Booking = () => {
    // State for page navigation
    const [currentPage, setCurrentPage] = useState('client-login');
    const [clientPassword, setClientPassword] = useState('');
    const [loginError, setLoginError] = useState('');

    // State for booking process
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [unavailableTimes, setUnavailableTimes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCurrentWeek, setIsCurrentWeek] = useState(true);
    const [isLastAvailableWeek, setIsLastAvailableWeek] = useState(false);
    const [allTerminDates, setAllTerminDates] = useState([]);    // State for confirmation form
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [comment, setComment] = useState('');

    const navigate = useNavigate();

    // Helper function to get the start of the current week (Monday)
    function getStartOfWeek(date) {
        // Create a new date object to avoid modifying the original
        const newDate = new Date(date);
        
        // Force the time to midnight to standardize comparisons
        newDate.setUTCHours(0, 0, 0, 0);
        
        const day = newDate.getUTCDay();
        // Calculate difference to Monday (1 = Monday in getUTCDay, so we need to handle Sunday differently)
        const diff = day === 0 ? newDate.getUTCDate() - 6 : newDate.getUTCDate() - day + 1;
        
        // Create a new UTC date at midnight
        return new Date(Date.UTC(newDate.getUTCFullYear(), newDate.getUTCMonth(), diff, 0, 0, 0, 0));
    }

    // Helper function to create UTC date without time component
    const createUTCDate = (year, month, day) => {
        return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    };

    // Format date as YYYY-MM-DD for API requests
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };

    // Check if two dates represent the same week
    const isSameWeek = (date1, date2) => {
        const week1Start = getStartOfWeek(date1);
        const week2Start = getStartOfWeek(date2);
        return week1Start.getTime() === week2Start.getTime();
    };

    // Get all available dates on component mount
    useEffect(() => {
        const fetchAllAvailableDates = async () => {
            try {
                const res = await apiClient.get("/termins");
                const data = res.data.data;

                const today = new Date();
                const utcToday = createUTCDate(
                    today.getUTCFullYear(),
                    today.getUTCMonth(),
                    today.getUTCDate()
                );

                // Calculate 14 days limit for client bookings
                const twoWeeksLater = new Date(utcToday);
                twoWeeksLater.setUTCDate(twoWeeksLater.getUTCDate() + 14);

                const dates = data
                    .filter(item => {
                        const itemDate = new Date(item.date);
                        // Filter for client: only show available dates within 14 days
                        return item.available && 
                               itemDate >= utcToday && 
                               itemDate <= twoWeeksLater;
                    })
                    .map(item => new Date(item.date));

                setAllTerminDates(dates);

                // Check if current week is the last available week
                checkIfLastAvailableWeek(dates, currentWeekStart);

            } catch (error) {
                console.error('Error fetching all available dates:', error);
                setAllTerminDates([]);
            }
        };

        fetchAllAvailableDates();
    }, []);

    // Check if the given week is the last available week
    const checkIfLastAvailableWeek = (dates, weekStart) => {
        if (dates.length === 0) {
            setIsLastAvailableWeek(true);
            return;
        }

        // Find the last available date
        const lastDate = new Date(Math.max(...dates.map(d => d.getTime())));
        const lastWeekStart = getStartOfWeek(lastDate);

        // Check if current week is the last week with available dates
        setIsLastAvailableWeek(weekStart.getTime() >= lastWeekStart.getTime());
    };    // Generate available dates for the current week
    useEffect(() => {
        const filterAvailableDatesForWeek = () => {
            try {
                const today = new Date();
                const utcToday = createUTCDate(
                    today.getUTCFullYear(),
                    today.getUTCMonth(),
                    today.getUTCDate()
                );

                // Calculate end of week (Sunday if week starts on Monday)
                const endOfWeek = new Date(currentWeekStart);
                endOfWeek.setUTCDate(endOfWeek.getUTCDate() + 6);
                
                // Both start and end should have precise time to ensure exact week boundaries
                const weekStart = new Date(Date.UTC(
                    currentWeekStart.getUTCFullYear(),
                    currentWeekStart.getUTCMonth(),
                    currentWeekStart.getUTCDate(),
                    0, 0, 0, 0
                ));
                
                const weekEnd = new Date(Date.UTC(
                    endOfWeek.getUTCFullYear(), 
                    endOfWeek.getUTCMonth(), 
                    endOfWeek.getUTCDate(),
                    23, 59, 59, 999
                ));

                // Filter allTerminDates for current week and remove duplicates
                const dates = allTerminDates
                    .filter(date => {
                        return date >= utcToday && 
                               date >= weekStart && 
                               date <= weekEnd;
                    })
                    // Remove duplicates by converting to timestamp and back
                    .filter((date, index, array) => {
                        return array.findIndex(d => d.getTime() === date.getTime()) === index;
                    })
                    // Sort dates chronologically
                    .sort((a, b) => a.getTime() - b.getTime());

                setAvailableDates(dates);
                
                // Check if this is the current week
                const todayWeekStart = getStartOfWeek(today);
                setIsCurrentWeek(todayWeekStart.getTime() === weekStart.getTime());
                
                // Check if this is the last available week
                checkIfLastAvailableWeek(allTerminDates, currentWeekStart);
                
            } catch (error) {
                console.error('Error filtering available dates:', error);
                setAvailableDates([]);
            }
        };

        // Only filter if we have allTerminDates loaded
        if (allTerminDates.length > 0) {
            filterAvailableDatesForWeek();
        }
    }, [currentWeekStart, allTerminDates]);

    // Function to navigate to previous week
    const goToPreviousWeek = () => {
        if (!isCurrentWeek) {
            const prevWeekStart = new Date(currentWeekStart);
            prevWeekStart.setUTCDate(prevWeekStart.getUTCDate() - 7);
            setCurrentWeekStart(prevWeekStart);
            
            // Clear selected date and time when changing weeks
            setSelectedDate(null);
            setSelectedTime(null);
            setAvailableTimes([]);
            setUnavailableTimes([]);
        }
    };

    // Function to navigate to next week
    const goToNextWeek = () => {
        if (!isLastAvailableWeek) {
            const nextWeekStart = new Date(currentWeekStart);
            nextWeekStart.setUTCDate(nextWeekStart.getUTCDate() + 7);
            setCurrentWeekStart(nextWeekStart);
            
            // Clear selected date and time when changing weeks
            setSelectedDate(null);
            setSelectedTime(null);
            setAvailableTimes([]);
            setUnavailableTimes([]);
        }
    };

    const fetchUnavailableTimes = async (date) => {
        try {
            const formattedDate = formatDateForAPI(date);
            const res = await apiClient.get("/termins/booked/", {
                params: { date: formattedDate }
            });
            setUnavailableTimes(res.data[formattedDate] || []);
        } catch (error) {
            console.error('Error fetching unavailable times:', error);
            setUnavailableTimes([]);
        } finally {
            setIsLoading(false);
        }
    };

    const generateAvailableTimes = async (date) => {
        setIsLoading(true);

        try {
            // Fetch both working hours and booked times in parallel
            const formattedDate = formatDateForAPI(date);
            const [hoursRes, bookedRes] = await Promise.all([
                apiClient.get("/termins/hours", {
                    params: { date: formattedDate }
                }),
                apiClient.get("/termins/booked/", {
                    params: { date: formattedDate }
                })
            ]);

            const { from, to } = hoursRes.data;
            const unavailableTimesForThisDate = bookedRes.data[formattedDate] || [];

            // Generate all possible time slots
            const allTimes = [];
            const startHour = parseInt(from.split(':')[0]);
            const endHour = parseInt(to.split(':')[0]);
            
            // Check if this is the current date
            const currentDate = new Date();
            const selectedDateTime = new Date(date);
            const isToday = 
                currentDate.getUTCFullYear() === selectedDateTime.getUTCFullYear() &&
                currentDate.getUTCMonth() === selectedDateTime.getUTCMonth() &&
                currentDate.getUTCDate() === selectedDateTime.getUTCDate();
            
            // Get current hours and minutes for today
            const currentHour = isToday ? currentDate.getHours() : 0;
            const currentMinute = isToday ? currentDate.getMinutes() : 0;

            for (let hour = startHour; hour < endHour; hour++) {
                const fullHour = `${hour.toString().padStart(2, '0')}:00`;
                const halfHour = `${hour.toString().padStart(2, '0')}:30`;

                // For current day, only show future times
                const isFullHourFuture = !isToday || (hour > currentHour || (hour === currentHour && 0 > currentMinute));
                const isHalfHourFuture = !isToday || (hour > currentHour || (hour === currentHour && 30 > currentMinute));

                if (!unavailableTimesForThisDate.includes(fullHour) && isFullHourFuture) {
                    allTimes.push(fullHour);
                }
                
                if (!unavailableTimesForThisDate.includes(halfHour) && isHalfHourFuture) {
                    allTimes.push(halfHour);
                }
            }

            setAvailableTimes(allTimes);
            setUnavailableTimes(unavailableTimesForThisDate);
        } catch (error) {
            console.error('Error fetching time slots:', error);
            setAvailableTimes([]);
            setUnavailableTimes([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClientLogin = async () => {
        try {
            const response = await apiClient.post("/auth/client-password", {
                password: clientPassword
            });
            if (response.data.isMatch) {
                resetBooking();
                setCurrentPage('booking');
                setLoginError('');            } else {
                setLoginError('Napačno geslo. Prosimo, poskusite znova.');
            }
        } catch (error) {
            console.error('Error during login:', error);
            setLoginError('Prišlo je do napake. Prosimo, poskusite znova.');
        }
    };

    const handleDateSelect = async (date) => {
        setSelectedDate(date);
        setSelectedTime(null);
        await generateAvailableTimes(date);
    };

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
    };    
    
    // Format the week display (e.g., "21. - 27. april 2025")
    const formatWeekDisplay = () => {
        const endOfWeek = new Date(currentWeekStart);
        endOfWeek.setUTCDate(endOfWeek.getUTCDate() + 6); // Add 6 days to get to Sunday
        
        const startMonth = currentWeekStart.toLocaleDateString('sl-SI', { month: 'long', timeZone: 'UTC' });
        const endMonth = endOfWeek.toLocaleDateString('sl-SI', { month: 'long', timeZone: 'UTC' });
        
        const startDay = currentWeekStart.getUTCDate();
        const endDay = endOfWeek.getUTCDate();
        const year = endOfWeek.getUTCFullYear();
        
        if (startMonth === endMonth) {
            return `${startDay}. - ${endDay}. ${startMonth} ${year}`;
        } else {
            return `${startDay}. ${startMonth} - ${endDay}. ${endMonth} ${year}`;
        }
    };    
    
    const formatDateDisplay = (date) => {
        return date.toLocaleDateString('sl-SI', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
        });
    };

    const formatLongSlovenianDate = (date) => {
        if (!date) return '';
        const formatted = date.toLocaleDateString('sl-SI', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC'
        });
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const handleContinue = () => {
        if (selectedDate && selectedTime) {
            setCurrentPage('confirmation');
        }
    };    
    const handleConfirmBooking = async () => {
        const bookingData = {
            firstName,
            lastName,
            phone,
            date: formatDateForAPI(selectedDate),
            time: selectedTime,
            comment: comment.trim() || null
        };

        await apiClient.post("/termins/booking", bookingData);

        setCurrentPage('thanks');
    };    
    
    const resetBooking = () => {
        setSelectedDate(null);
        setSelectedTime(null);
        setFirstName('');
        setLastName('');
        setPhone('');
        setComment('');
        setClientPassword('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleClientLogin();
        }
    };
    
    // Progress bar helper functions
    const getProgressPercentage = () => {
        switch (currentPage) {
            case 'booking':
                if (selectedDate && selectedTime) return 45;
                if (selectedDate) return 25;
                return 10;
            case 'confirmation':
                if (firstName && lastName && phone) return 85;
                return 65;
            case 'thanks':
                return 100;
            default:
                return 0;
        }
    };

    const renderProgressBar = () => {
        if (currentPage === 'client-login') return null;

        return (
            <div className="progress-bar-container">
                <div 
                    className="progress-bar" 
                    style={{ width: `${getProgressPercentage()}%` }}
                ></div>
            </div>
        );
    };

    const renderClientLogin = () => (
        <div className="login-page" id="client-login">
            <div className="site-branding">
                <div className="site-info">
                    <div className="site-title">POKOSIMO.COM</div>
                    <div className="site-subtitle">REZERVACIJE</div>
                </div>
            </div>

            <div className="background-overlay"></div>

            <div className="login-content">
                <div className="page-header">
                    <h1>VNESITE GESLO</h1>
                </div>

                <div className="login-form">
                    <div className="input-group">
                        <i className="fas fa-lock input-icon"></i>
                        <input
                            type="password"
                            placeholder="Vnesite geslo"
                            value={clientPassword}
                            onChange={(e) => setClientPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                    <div className="btn-group">
                        <button
                            onClick={handleClientLogin}
                            disabled={!clientPassword}
                            id="client-login-btn"
                            className={`btn-primary ${clientPassword ? "" : "cant-login"}`}
                        >
                            PRIJAVA <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>
                    {loginError && <p className="error">{loginError}</p>}
                </div>
            </div>
        </div>
    );

    const renderBookingPage = () => (        <div className="booking-page">
            <div className="site-branding">
                <div className="site-info">
                    <div className="site-title">POKOSIMO.COM</div>
                    <div className="site-subtitle">REZERVACIJE</div>
                </div>
            </div>

            <div className="background-overlay"></div>

            <div className="booking-container">                <div className="date-selector">
                    <div className="month-display">
                        <span>{formatWeekDisplay()}</span>
                    </div>
                    <div className="date-navigation">                        <button 
                            className={`nav-button ${isCurrentWeek ? 'disabled' : ''}`}
                            onClick={goToPreviousWeek} 
                            disabled={isCurrentWeek}
                        >
                            ← Prejšnji teden
                        </button>
                        <button 
                            className={`nav-button ${isLastAvailableWeek ? 'disabled' : ''}`}
                            onClick={goToNextWeek} 
                            disabled={isLastAvailableWeek}
                        >
                            Naslednji teden →
                        </button>
                    </div>
                </div>                <div className="date-cards">
                    {availableDates.map((date, index) => (                        <div
                            key={index}
                            className={`date-card ${selectedDate?.toDateString() === date.toDateString() ? 'selected' : ''}`}
                            onClick={() => handleDateSelect(date)}
                        >
                            <div className="date-day">{date.toLocaleDateString('sl-SI', { weekday: 'short', timeZone: 'UTC' }).toUpperCase()}</div>
                            <div className="date-number">{date.getUTCDate()}</div>
                            <div className="date-day">{date.toLocaleDateString('sl-SI', { month: 'short', timeZone: 'UTC' }).toUpperCase()}</div>
                        </div>
                    ))}
                    {availableDates.length === 0 && (
                        <div className="no-slots-message">Ni razpoložljivih terminov za ta teden</div>
                    )}
                </div>                {isLoading ? (
                    <div className="loading-indicator">                        <div className="loading-spinner"></div>
                        <div className="loading-text">Nalaganje razpoložljivih terminov...</div>
                    </div>                ) : selectedDate && (
                    <div className="time-slots">
                        <h3>RAZPOLOŽLJIVI TERMINI</h3>
                        <div className="time-slots-grid">
                            {availableTimes.map((time, index) => (
                                <button
                                    key={index}
                                    className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                                    onClick={() => handleTimeSelect(time)}
                                >
                                    <span className="time-value">{time}</span>
                                </button>
                            ))}
                            {unavailableTimes.map((time, index) => (
                                <button
                                    key={`unavailable-${index}`}
                                    className="time-slot unavailable"
                                    disabled
                                    title="Ta termin je že zaseden"
                                >
                                    <span className="time-value">{time}</span>
                                </button>
                            ))}
                            {availableTimes.length === 0 && !isLoading && (
                                <div className="no-slots-message">Za ta datum ni razpoložljivih terminov</div>
                            )}
                        </div>
                    </div>
                )}

                {(selectedDate && selectedTime) && (                    <div className="booking-summary-section">                        <div className="summary-title">REZERVACIJA</div>
                        <div className="summary-row">
                            <span className="summary-label">Datum:</span>
                            <span className="summary-value">{formatLongSlovenianDate(selectedDate)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Čas:</span>
                            <span className="summary-value">{selectedTime}</span>
                        </div>
                    </div>
                )}

                <div className="booking-actions">                    <button 
                        className="btn-secondary minimized-logout"
                        onClick={() => {
                            resetBooking();
                            setCurrentPage('client-login');
                        }}
                    >
                        ODJAVA
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleContinue}
                        disabled={!selectedDate || !selectedTime}
                    >
                        NADALJUJ
                    </button>
                </div>
            </div>
        </div>
    );

    const renderConfirmationPage = () => (        <div className="booking-page">
            <div className="confirmation-page">
                <div className="site-branding">
                    <div className="site-info">
                        <div className="site-title">POKOSIMO.COM</div>
                        <div className="site-subtitle">REZERVACIJE</div>
                    </div>
                </div>
                
                <div className="background-overlay"></div>                <div className="page-header">
                    <h1>ZAKLJUČITE VAŠO REZERVACIJO</h1>
                    <p>Prosimo, vnesite svoje podatke za potrditev termina</p>
                </div>

                <div className="confirmation-form">                    <div className="form-group">
                        <label>IME</label>
                        <input
                            type="text"
                            placeholder="Vnesi ime"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>PRIIMEK</label>
                        <input
                            type="text"
                            placeholder="Vnesi priimek"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>                    <div className="form-group">
                        <label>TELEFONSKA ŠTEVILKA</label>
                        <input
                            type="tel"
                            inputMode="tel"
                            placeholder="Vnesi tel. številko"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            pattern="[0-9]*"
                        />
                    </div>                    <div className="form-group">
                        <label>KOMENTARJI (NEOBVEZNO)</label>
                        <input
                            type="text"
                            placeholder="Dodaj komentar (neobvezno)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            maxLength={500}
                        />
                    </div>

                    <div className="booking-summary">
                        <h3>PODROBNOSTI REZERVACIJE</h3>
                        <div className="summary-row">
                            <span className="summary-label">Datum:</span>
                            <span className="summary-value">{formatLongSlovenianDate(selectedDate)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Čas:</span>
                            <span className="summary-value">{selectedTime}</span>
                        </div>
                        {comment && (
                            <div className="summary-row">
                                <span className="summary-label">Komentar:</span>
                                <span className="summary-value">{comment}</span>
                            </div>
                        )}
                    </div>                    <div className="confirmation-actions">
                        <button
                            className="btn-primary"
                            onClick={handleConfirmBooking}
                            disabled={!firstName || !lastName || !phone}
                        >
                            POTRDI REZERVACIJO
                        </button>
                        <button 
                            className="btn-secondary"
                            onClick={() => setCurrentPage('booking')}
                        >
                            NAZAJ
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderThanksPage = () => (        <div className="thanks-page">
            <div className="site-branding">
                <div className="site-info">
                    <div className="site-title">POKOSIMO.COM</div>
                    <div className="site-subtitle">REZERVACIJE</div>
                </div>
            </div>
            
            <div className="background-overlay"></div>
            <div className="page-header">                <h1>HVALA!</h1>
                <p>Vaša rezervacija je bila uspešno zabeležena.</p>
            </div>

            <div className="confirmation-form">                <div className="booking-summary">                    <h3>VAŠ TERMIN JE POTRJEN</h3>
                    <div className="summary-row">
                        <span className="summary-label">Datum:</span>
                        <span className="summary-value">{formatLongSlovenianDate(selectedDate)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="summary-label">Čas:</span>
                        <span className="summary-value">{selectedTime}</span>
                    </div>
                    <div className="summary-row">
                        <span className="summary-label">Ime:</span>
                        <span className="summary-value">{firstName} {lastName}</span>
                    </div>
                    {comment && (
                        <div className="summary-row">
                            <span className="summary-label">Komentar:</span>
                            <span className="summary-value">{comment}</span>
                        </div>
                    )}
                </div>                  <div className="thanks-actions">
                    <button 
                        className="btn-primary"
                        onClick={() => {
                            resetBooking();
                            setCurrentPage('client-login');
                        }}
                    >
                        REZERVIRAJ NOV TERMIN
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="booking-system">
            {renderProgressBar()}
            {currentPage === 'client-login' && renderClientLogin()}
            {currentPage === 'booking' && renderBookingPage()}
            {currentPage === 'confirmation' && renderConfirmationPage()}
            {currentPage === 'thanks' && renderThanksPage()}
        </div>
    );
};

export default Booking;